// Upcoming Jobs Modal for Facility Dashboard
// Shows list of upcoming approved jobs with check-in functionality

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Modal,
  Pressable,
} from 'react-native';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import QRCode from 'react-native-qrcode-svg';
import { getJobQR } from '../../services/job';
import { webColors, webBorderRadius, webShadows } from '../../constants/webStyles';
import { useIsMobile } from '../../hooks/useIsMobile';

const UpcomingJobsModal = ({ visible, jobs = [], onClose, onCheckIn }) => {
  const [selectedJobForQR, setSelectedJobForQR] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const isMobile = useIsMobile();

  // Fetch QR code for selected job
  const {
    data: qrData,
    isLoading: qrLoading,
    refetch: refetchQR,
  } = useQuery({
    queryKey: ['jobQR', selectedJobForQR?.uuid],
    queryFn: () => getJobQR(selectedJobForQR?.uuid),
    enabled: !!selectedJobForQR?.uuid,
  });

  const handleCheckIn = (job) => {
    setSelectedJobForQR(job);
    setShowQRModal(true);
  };

  const closeQRModal = () => {
    setShowQRModal(false);
    setSelectedJobForQR(null);
  };

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable style={styles.overlay} onPress={onClose}>
        <Pressable style={styles.modal} onPress={e => e.stopPropagation()}>
          {/* Header with pink gradient */}
          <View style={styles.header}>
            <Text style={styles.headerTitle}>Upcoming Locum Jobs</Text>
            <TouchableOpacity style={styles.closeBtn} onPress={onClose}>
              <Text style={styles.closeBtnText}>×</Text>
            </TouchableOpacity>
          </View>

          {/* Body */}
          <ScrollView style={styles.body} showsVerticalScrollIndicator={false}>
            {jobs.length > 0 ? (
              jobs.map((job, index) => (
                <View key={job.uuid || index} style={styles.jobItem}>
                  <View style={styles.jobItemHeader}>
                    {/* Date Pill */}
                    <View style={styles.datePillSmall}>
                      <Text style={styles.datePillMonth}>
                        {moment(job.start_timestamp).format('MMM').toUpperCase()}
                      </Text>
                      <Text style={styles.datePillDay}>
                        {moment(job.start_timestamp).format('D')}
                      </Text>
                    </View>

                    {/* Job Details */}
                    <View style={styles.jobDetails}>
                      <Text style={styles.jobTitle} numberOfLines={1}>
                        {job.title}
                      </Text>
                      <Text style={styles.jobClinic}>
                        {job.clinic?.name || job.clinic_name || 'Healthcare Facility'}
                      </Text>
                      <Text style={styles.jobTime}>
                        ⏰ {moment(job.start_timestamp).format('h:mm A')} - {moment(job.end_timestamp).format('h:mm A')}
                      </Text>
                      {job.incentives && (
                        <Text style={styles.jobIncentives}>
                          {job.incentives}
                        </Text>
                      )}
                    </View>
                  </View>

                  {/* Check-in Actions */}
                  <View style={styles.jobActions}>
                    {job.is_scanned ? (
                      <View style={styles.checkedInBadge}>
                        <Text style={styles.checkedInText}>✓ Checked In</Text>
                      </View>
                    ) : (
                      <View style={[styles.checkInBtnRow, isMobile && styles.checkInBtnRowMobile]}>
                        {onCheckIn && (
                          <TouchableOpacity
                            style={styles.directCheckInBtn}
                            onPress={() => onCheckIn(job)}
                          >
                            <Text style={styles.directCheckInBtnText}>Direct Check In</Text>
                          </TouchableOpacity>
                        )}
                        <TouchableOpacity
                          style={styles.showQRBtn}
                          onPress={() => handleCheckIn(job)}
                        >
                          <Text style={styles.showQRBtnText}>Show QR</Text>
                        </TouchableOpacity>
                      </View>
                    )}
                  </View>
                </View>
              ))
            ) : (
              <View style={styles.emptyState}>
                <Text style={styles.emptyStateIcon}>📅</Text>
                <Text style={styles.emptyStateText}>No upcoming jobs</Text>
              </View>
            )}
          </ScrollView>
        </Pressable>
      </Pressable>

      {/* QR Code Modal */}
      <Modal
        visible={showQRModal}
        transparent
        animationType="fade"
        onRequestClose={closeQRModal}
      >
        <Pressable style={styles.overlay} onPress={closeQRModal}>
          <Pressable style={styles.qrModal} onPress={e => e.stopPropagation()}>
            <View style={styles.qrHeader}>
              <Text style={styles.qrHeaderTitle}>Check-in QR Code</Text>
              <TouchableOpacity style={styles.closeBtn} onPress={closeQRModal}>
                <Text style={styles.closeBtnText}>×</Text>
              </TouchableOpacity>
            </View>
            <View style={styles.qrBody}>
              {qrLoading ? (
                <ActivityIndicator size="large" color={webColors.primary} />
              ) : qrData?.data?.data?.qr ? (
                <>
                  <QRCode
                    value={qrData.data.data.qr}
                    size={250}
                    logoBackgroundColor="transparent"
                  />
                  <Text style={styles.qrInstructions}>
                    Have the doctor scan this code to check in
                  </Text>
                  {selectedJobForQR && (
                    <View style={styles.qrJobInfo}>
                      <Text style={styles.qrJobTitle}>{selectedJobForQR.title}</Text>
                      <Text style={styles.qrJobTime}>
                        {moment(selectedJobForQR.start_timestamp).format('MMM D, h:mm A')}
                      </Text>
                    </View>
                  )}
                </>
              ) : (
                <Text style={styles.qrError}>Unable to load QR code</Text>
              )}
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modal: {
    backgroundColor: webColors.cardBackground,
    borderRadius: 20,
    width: '100%',
    maxWidth: 500,
    maxHeight: '80vh',
    overflow: 'hidden',
    ...webShadows.modal,
  },
  header: {
    background: 'linear-gradient(135deg, #EC4899 0%, #f472b6 100%)',
    backgroundColor: '#EC4899',
    padding: 16,
    paddingHorizontal: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#ffffff',
  },
  closeBtn: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeBtnText: {
    fontSize: 18,
    color: '#ffffff',
    lineHeight: 20,
  },
  body: {
    padding: 16,
    maxHeight: '60vh',
  },
  jobItem: {
    backgroundColor: '#f9fafb',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  jobItemHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
  },
  datePillSmall: {
    backgroundColor: '#ffffff',
    borderRadius: 8,
    padding: 8,
    paddingHorizontal: 12,
    alignItems: 'center',
    minWidth: 50,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
  },
  datePillMonth: {
    fontSize: 9,
    fontWeight: '600',
    color: '#EC4899',
    textTransform: 'uppercase',
  },
  datePillDay: {
    fontSize: 18,
    fontWeight: '700',
    color: '#EC4899',
    lineHeight: 22,
  },
  jobDetails: {
    flex: 1,
  },
  jobTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 2,
  },
  jobClinic: {
    fontSize: 13,
    color: webColors.textSecondary,
    marginBottom: 6,
  },
  jobTime: {
    fontSize: 12,
    color: webColors.textMuted,
  },
  jobIncentives: {
    fontSize: 11,
    color: '#22c55e',
    fontWeight: '500',
    marginTop: 4,
  },
  jobActions: {
    alignItems: 'flex-start',
  },
  checkInBtnRow: {
    flexDirection: 'row',
    gap: 8,
  },
  directCheckInBtn: {
    backgroundColor: '#22c55e',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: webBorderRadius.medium,
  },
  directCheckInBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
  showQRBtn: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: webBorderRadius.medium,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  showQRBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6b7280',
  },
  checkInBtn: {
    backgroundColor: '#22c55e',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: webBorderRadius.medium,
  },
  checkInBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#ffffff',
  },
  checkedInBadge: {
    backgroundColor: '#dcfce7',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  checkedInText: {
    fontSize: 12,
    color: '#22c55e',
    fontWeight: '600',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyStateIcon: {
    fontSize: 48,
    marginBottom: 12,
    opacity: 0.5,
  },
  emptyStateText: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  // QR Modal
  qrModal: {
    backgroundColor: webColors.cardBackground,
    borderRadius: 20,
    width: '100%',
    maxWidth: 400,
    overflow: 'hidden',
    ...webShadows.modal,
  },
  qrHeader: {
    padding: 16,
    paddingHorizontal: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  qrHeaderTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
  },
  qrBody: {
    padding: 24,
    alignItems: 'center',
  },
  qrInstructions: {
    fontSize: 14,
    color: webColors.textSecondary,
    textAlign: 'center',
    marginTop: 20,
  },
  qrJobInfo: {
    marginTop: 16,
    alignItems: 'center',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: webColors.borderLight,
    width: '100%',
  },
  qrJobTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  qrJobTime: {
    fontSize: 13,
    color: webColors.textMuted,
    marginTop: 4,
  },
  qrError: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  // ==================== MOBILE VARIANTS ====================
  checkInBtnRowMobile: {
    flexWrap: 'wrap',
    rowGap: 8,
  },
});

export default UpcomingJobsModal;
